// Everything edible the recipe chat can cook with, from the app's own guides.
// Garden plants come from the user's garden, finds from their map; this file
// builds the catalogue part and marks what is wild. Houseplants and garden
// flowers are never offered as food. Wild plants and every mushroom need the
// "I am sure of the identification" box; the server enforces the same rule.
import { PLANT_CATALOG } from '../data';
import { MUSHROOMS } from '../mushrooms';
import { FORAGING } from '../foraging';

export const GROUPS = ['vegetable', 'fruit', 'berry', 'nut', 'herb', 'wild', 'mushroom'];
const PLANT_GROUP = { vegetable: 'vegetable', fruit: 'fruit', berry: 'berry', nut: 'nut', herb: 'herb', mushroom: 'mushroom' };
// Exotic crops are food too; most are fruit, a few are spices or vegetables.
const EXOTIC_HERB = new Set(['kaffe', 'te', 'kakao', 'vanilje', 'ingefaer', 'gurkemeje', 'kaffirlime', 'karryblad', 'safran', 'wasabi']);
const EXOTIC_VEG = new Set(['moringa', 'yacon', 'chayote', 'oliven']);
const MUSHROOM_IDS = new Set(MUSHROOMS.map(m => m.id));

const pick = (value, locale) => (value && typeof value === 'object' ? value[locale] || value[locale?.split('-')[0]] || value.en || value.da : value) || '';
const fold = value => String(value || '').toLocaleLowerCase('da').normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/ø/g, 'o').replace(/æ/g, 'ae').replace(/å/g, 'aa');

export const isWild = source => source.kind === 'find' || source.group === 'wild' || source.group === 'mushroom';

function plantGroup(plant) {
  if (plant.cat === 'exotic') return EXOTIC_HERB.has(plant.id) ? 'herb' : EXOTIC_VEG.has(plant.id) ? 'vegetable' : 'fruit';
  return PLANT_GROUP[plant.cat] || null;
}

const cache = new Map();
export function edibleCatalog(locale = 'da') {
  if (cache.has(locale)) return cache.get(locale);
  const seen = new Set();
  const out = [];
  const add = entry => {
    const key = fold(entry.name);
    if (!entry.name || seen.has(key)) return;
    seen.add(key);
    out.push(entry);
  };
  // The nature guides first: where a name exists twice, the wild entry (with
  // its lookalike warning and the certainty box) wins.
  for (const m of MUSHROOMS) add({ kind: 'catalog', id: `mushroom:${m.id}`, name: pick(m.name, locale), group: 'mushroom', emoji: m.emoji || '\u{1F344}', lookalike: pick(m.lookalike_warning, locale) || null });
  for (const f of FORAGING) add({ kind: 'catalog', id: `wild:${f.id}`, name: pick(f.name, locale), group: 'wild', emoji: f.emoji || '\u{1F33F}', lookalike: pick(f.lookalike_warning, locale) || null });
  for (const p of PLANT_CATALOG) {
    const group = plantGroup(p);
    if (group) add({ kind: 'catalog', id: p.id, name: pick(p.name, locale), group, emoji: p.emoji || '\u{1F331}', lookalike: null });
  }
  cache.set(locale, out);
  return out;
}

export function searchCatalog(catalog, query, limit = 40) {
  const q = fold(query).trim();
  if (!q) return catalog.slice(0, limit);
  return catalog.filter(s => fold(s.name).includes(q)).slice(0, limit);
}

export function findSource(find) {
  return { kind: 'find', id: find.id, name: String(find.species_name || '').slice(0, 80),
    group: MUSHROOM_IDS.has(find.species_id) ? 'mushroom' : 'wild' };
}

// What the server needs; the rest (emoji, lookalike) stays in the app.
export const toRequestSource = s => ({ kind: s.kind, id: s.id.slice(0, 80), name: s.name.slice(0, 80), group: s.group });

// The food group of a garden species (from the plant catalogue), or null for
// houseplants, flowers and unknown species. Not affected by name de-duplication.
export function speciesGroup(speciesId) {
  const plant = speciesId && PLANT_CATALOG.find(p => p.id === speciesId);
  return plant ? plantGroup(plant) : null;
}

// A garden plant whose species is food (not a houseplant or a flower).
export function isEdibleSpecies(speciesId) {
  return !!speciesGroup(speciesId);
}
